import React, { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useRecoilValue } from "recoil";
import MultiSelectInputForJobConfig from "../../../../components/MultiSelectInputForJobConfig";
import SelectHookFormSingle from "../../../../components/SelectHookFormSingle";
import TextInputHookFormDisabled from "../../../../components/TextInputHookFormDisabled";
import TextInputLabelHookForm from "../../../../components/TextInputLabelHookForm";
import TimeInput from "../../../../components/TimeInput";
import { addShowState, editState } from "../atoms/atoms";
import { IJobsDetails } from "../types";
import JobReportDetails from "./JobReportDetails";

interface JobFormProps {
  rowToEdit: IJobsDetails | undefined;
  scheduleDaysOpts: [];
  monthDaysOpts: any[];
}
const JobFormDetails: React.FC<JobFormProps> = ({
  rowToEdit,
  scheduleDaysOpts,
  monthDaysOpts,
}) => {
  const {
    register,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useFormContext();

  const actionAddState = useRecoilValue(addShowState);
  const actionEditState = useRecoilValue(editState);
  const [isSchedDays, setIsSchedDays] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);

  const serverInstanceOptions = [
    { value: 1, label: "1" },
    { value: 2, label: "2" },
  ];
  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];
  const withEmailOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No" },
  ];

  const recurringOptions: any = [
    { value: "Every minute", label: "Every minute" },
    { value: "Every hour", label: "Every hour" },
    { value: "every nth day of a Month", label: "every nth day of a Month" },
  ];

  const minutesOps = [
    { value: "1", label: "1" },
    { value: "2", label: "2" },
    { value: "3", label: "3" },
    { value: "4", label: "4" },
    { value: "5", label: "5" },
    { value: "6", label: "6" },
    { value: "10", label: "10" },
    { value: "15", label: "15" },
    { value: "20", label: "20" },
    { value: "30", label: "30" },
  ];
  const hoursOpts = Array.from({ length: 24 }, (_, i) => ({
    value: (i + 1).toString(),
    label: (i + 1).toString(),
  }));

  const timeValueOps = watch("recurring")
    ? watch("recurring")?.includes("minute")
      ? minutesOps
      : hoursOpts
    : [];

  const scheduleDaysValue = watch("scheduleDays");
  const recurringValue = watch("recurring");
  const withEmailValue = watch("withEmail");

  useEffect(() => {
    if (!scheduleDaysValue && !recurringValue) {
      setIsSchedDays(true);
      setIsRecurring(true);
    } else if (recurringValue) {
      setIsRecurring(true);
      setIsSchedDays(false);
    } else if (scheduleDaysValue) {
      setIsSchedDays(true);
      setIsRecurring(false);
    }

    if (!scheduleDaysValue) {
      setValue("scheduleTime", "");
    }

    if (!recurringValue) {
      setValue("timeValue", "");
    }
  }, [scheduleDaysValue, recurringValue, setValue]);

  return (
    <div>
      <div>
        <div className="m-2 bg-white box-shadow rounded-lg h-full">
          <div className="text-quinary font-bold font-sans py-2 px-4">
            Event Details
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 px-4 pb-2 gap-2">
            <div className="space-y-1 mr-4">
              <TextInputLabelHookForm
                name="eventName"
                label="Event Name"
                type="text"
                register={register("eventName", {
                  required: {
                    value: true,
                    message: "Required Field",
                  },
                })}
                maxLength={50}
                required={true}
                className={
                  errors.eventName && "border-2 border-red-500 col-span-2 "
                }
              />
              <Controller
                control={control}
                rules={{ required: isSchedDays }}
                name="scheduleDays"
                render={({ field }) => (
                  <MultiSelectInputForJobConfig
                    label="Schedule Days"
                    isRequired={isSchedDays}
                    options={scheduleDaysOpts ? scheduleDaysOpts : []}
                    handleMultiSelect={(e) => {
                      const value = e.length === 0 ? null : e;
                      field.onChange(value);
                    }}
                    defaultValue={undefined}
                    className={
                      errors.scheduleDays &&
                      "border-2 border-red-500 col-span-2 "
                    }
                    value={field.value}
                    isDisabled={recurringValue}
                  />
                )}
              />
              <TimeInput
                name="scheduleTime"
                label="Schedule Time"
                type="time"
                maxLength={50}
                register={register("scheduleTime", {
                  required: {
                    value: scheduleDaysValue,
                    message: "Required Field",
                  },
                })}
                required={scheduleDaysValue}
                disabled={isRecurring}
                onChange={(e) => {
                  setValue("scheduleTime", e ? e.target.value : null);
                }}
                className={
                  errors.scheduleTime && "border-2 border-red-500 col-span-2 "
                }
              />
              <Controller
                control={control}
                rules={{ required: isRecurring }}
                name="recurring"
                render={({ field }) => (
                  <SelectHookFormSingle
                    label="Recurring"
                    required={isRecurring}
                    options={recurringOptions}
                    onChange={(e) => {
                      field.onChange(e ? e.value : "");
                    }}
                    defaultValue={undefined}
                    className={
                      errors.recurring && "border-2 border-red-500 col-span-2 "
                    }
                    isDisabled={scheduleDaysValue}
                    value={field.value}
                  />
                )}
              />
              <Controller
                control={control}
                rules={{ required: false }}
                name="timeValue"
                render={({ field }) => (
                  <SelectHookFormSingle
                    label="Time Value"
                    required={watch("recurring") ? true : false}
                    options={timeValueOps}
                    onChange={(e) => {
                      field.onChange(e ? e.value : "");
                    }}
                    defaultValue={undefined}
                    className={
                      errors.recurring && "border-2 border-red-500 col-span-2 "
                    }
                    isDisabled={!watch("recurring") ? true : false}
                    value={field.value}
                    placeHolder={
                      !watch("recurring")
                        ? "Select Recurring to enable"
                        : undefined
                    }
                  />
                )}
              />
              <Controller
                control={control}
                rules={{ required: false }}
                name="monthDayValue"
                render={({ field }) => (
                  <SelectHookFormSingle
                    label="Day of Month Value"
                    required={
                      watch("recurring") &&
                      watch("recurring").includes("nth day")
                        ? true
                        : false
                    }
                    options={monthDaysOpts}
                    onChange={(e) => {
                      field.onChange(e ? e.value : "");
                    }}
                    defaultValue={undefined}
                    className={
                      errors.recurring && "border-2 border-red-500 col-span-2 "
                    }
                    isDisabled={
                      !watch("recurring") ||
                      !watch("recurring").includes("nth day")
                        ? true
                        : false
                    }
                    value={field.value}
                    placeHolder={
                      !watch("recurring")
                        ? "Select Recurring to enable"
                        : undefined
                    }
                  />
                )}
              />
            </div>
            <div className="space-y-1 w-full text-xs">
              <div className="space-y-1 mr-4">
                <TextInputLabelHookForm
                  name="subject"
                  label="Subject"
                  type="text"
                  maxLength={50}
                  required={withEmailValue === true}
                  register={register("subject", {
                    required:
                      withEmailValue === true
                        ? {
                            value: true,
                            message: "Required Field",
                          }
                        : false,
                  })}
                  className={
                    errors.subject && "border-2 border-red-500 col-span-2 "
                  }
                />
                <Controller
                  control={control}
                  rules={{ required: true }}
                  name="serverInstance"
                  render={({ field }) => (
                    <SelectHookFormSingle
                      field={field}
                      onChange={(e) => {
                        field.onChange(e ? e.value : null);
                      }}
                      value={field.value}
                      options={serverInstanceOptions}
                      label="Server Instance"
                      required={true}
                      className={
                        errors.serverInstance &&
                        "border-2 border-red-500 col-span-2 "
                      }
                    />
                  )}
                />
                <Controller
                  control={control}
                  rules={{ required: false }}
                  name="withEmail"
                  render={({ field }) => (
                    <SelectHookFormSingle
                      field={field}
                      onChange={(e) => {
                        field.onChange(e ? e.value : null);
                      }}
                      value={field.value}
                      options={withEmailOptions}
                      label="With Email"
                      required={false}
                      className={
                        errors.withEmail &&
                        "border-2 border-red-500 col-span-2 "
                      }
                    />
                  )}
                />
                <Controller
                  control={control}
                  rules={{
                    validate: (value) => value === true || value === false,
                  }}
                  name="isActive"
                  render={({ field }) => (
                    <SelectHookFormSingle
                      field={field}
                      onChange={(e) => {
                        field.onChange(e ? e.value : null);
                      }}
                      value={field.value}
                      options={statusOptions}
                      label="Status"
                      required={true}
                      isDisabled={actionAddState ? true : false}
                      className={
                        errors.isActive && "border-2 border-red-500 col-span-2 "
                      }
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>
        <JobReportDetails
          reportsDetails={
            rowToEdit?.configReports && rowToEdit.configReports?.length > 0
              ? rowToEdit.configReports
              : undefined
          }
        />
        {actionEditState && (
          <div className="m-2 bg-white box-shadow rounded-lg h-full">
            <div className="text-quinary font-bold font-sans py-2 px-4">
              System Information
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 px-4 pb-2 gap-2">
              <div className="space-y-1 mr-2">
                <TextInputHookFormDisabled
                  label="Created By"
                  name="createdBy"
                  register={register("createdBy", {})}
                />
                <TextInputHookFormDisabled
                  label="Created Date"
                  name="createdDate"
                  register={register("createdDate", {})}
                />
              </div>
              <div className="space-y-1 mr-2">
                <TextInputHookFormDisabled
                  label="Modified By"
                  name="modifiedBy"
                  register={register("modifiedBy", {})}
                />
                <TextInputHookFormDisabled
                  label="Modified Date"
                  name="modifiedDate"
                  register={register("modifiedDate", {})}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobFormDetails;
